// ---------------------------------------------------------------------------
// Tab 2 — Payment Details (read-only)
// ---------------------------------------------------------------------------

import { Badge } from '@/components/data-display/Badge';
import type { PaymentDetail } from './types';

interface Props {
  payment: PaymentDetail;
}

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pending Review',
  processing: 'Processing',
  completed: 'Completed',
  held: 'On Hold',
  declined: 'Declined',
  failed: 'Failed',
};

function formatAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between border-b border-[#f0f0f0] py-2.5 last:border-b-0">
      <span className="text-[13px] font-medium text-[#888]">{label}</span>
      <span className="text-right text-[13px] font-semibold text-[#333]">{children}</span>
    </div>
  );
}

export function PaymentDetailsTab({ payment }: Props) {
  // Fees are cumulative (#66), so both terms can apply at once. Show only the
  // parts that are actually charged — "None" when the rail is free for this
  // client, which is a legitimate arrangement rather than missing data.
  const feeTerms = [
    payment.fee_fixed_value > 0 ? formatAmount(payment.fee_fixed_value, payment.currency) : null,
    payment.fee_percent_value > 0 ? `${payment.fee_percent_value}%` : null,
  ].filter(Boolean);
  const feeDisplay = feeTerms.length > 0 ? feeTerms.join(' + ') : 'None';

  const totalAmount = payment.amount + payment.fee_applied;

  return (
    <div className="space-y-1">
      <Field label="Operation ID">{payment.convera_operation_id}</Field>
      <Field label="Status">
        <Badge
          variant={
            payment.status === 'completed'
              ? 'success'
              : payment.status === 'pending'
                ? 'warning'
                : payment.status === 'processing'
                  ? 'info'
                  : 'default'
          }
        >
          {STATUS_LABEL[payment.status] ?? payment.status}
        </Badge>
      </Field>
      <Field label="Fee Terms">{feeDisplay}</Field>
      <Field label="Fee Applied">{formatAmount(payment.fee_applied, payment.currency)}</Field>
      <Field label="Total Amount">{formatAmount(totalAmount, payment.currency)}</Field>
    </div>
  );
}
